import { Modal, ModalType, ProfileMenuItem } from '@code/innovance-react-ui-kit';
import { AxiosResponse } from 'axios';
import React, {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { apiInstance, Logout, updateHeaderConfig } from 'src/front/api/apiInstance';

import { Menu } from 'src/front/api/generated';
import {
  getRejectReason,
  isIgnoredError,
  isPreLoginError,
  isSessionEndingError,
} from 'src/front/common/utils/errorUtils';

import { LOGIN } from 'src/front/routes/paths';

interface UserInfoState {
  isLoggedIn: boolean;
  userCode?: string;
  userProfileImageName?: string;
  processDate?: Date;
  isAuthenticationRestricted?: boolean;
}
interface SessionState {
  isLoggedIn: boolean;
  userCode?: string;
  userName?: string;
  userProfileImageName?: string;
  menu?: Menu[];
  processDate?: Date;
  isAuthenticationRestricted?: boolean;
  profileMenu: Menu[];
}

type UserNameState = string;

type UserInfoDispatch = Dispatch<SetStateAction<UserInfoState>>;
type MenuDispatch = Dispatch<SetStateAction<Menu[]>>;
type ProfileMenuDispatch = Dispatch<SetStateAction<Menu[]>>;
type UserNameDispatch = Dispatch<SetStateAction<UserNameState>>;
interface SessionDispatch {
  setUserInfo: UserInfoDispatch;
  setMenu: MenuDispatch;
  setProfileMenu: ProfileMenuDispatch;
  setUserName: UserNameDispatch;
}

const SessionStateContext = createContext<SessionState | undefined>(undefined);
const SessionDispatchContext = createContext<SessionDispatch | undefined>(undefined);

const SessionProvider: FC = ({ children }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [userInfo, setUserInfo] = useState<UserInfoState>({ isLoggedIn: false });
  const [userName, setUserName] = useState<UserNameState>('');
  const [menu, setMenu] = useState<Menu[]>([]);
  const [profileMenu, setProfileMenu] = useState<Menu[]>([]);
  const [errorConfig, setErrorConfig] = useState<{
    showError: boolean;
    error?: string | null | undefined;
    isAuthError?: boolean;
  }>();

  const handleModalClose = (): void => {
    setErrorConfig({ showError: false });
  };

  const handleLogout = async (): Promise<void> => {
    await Logout.logout();

    updateHeaderConfig();
    setUserInfo({ isLoggedIn: false });
    setErrorConfig({ showError: false });

    history.push(LOGIN);
  };

  useEffect(() => {
    apiInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        const {
          data: { result, error },
          status,
        } = response;

        // we've to check that request is valid because we ignored validationStatus
        if (status >= 200 && status < 300) {
          if (result === 'ERROR') {
            setErrorConfig({ showError: true, error: error?.errorMessage });
          }

          return response;
        }

        // due to backwards compatibility
        /* eslint-disable prefer-promise-reject-errors */
        if (isPreLoginError(error)) {
          return Promise.reject({ response });
        }

        if (status === 401 && isSessionEndingError(error)) {
          setErrorConfig({
            showError: true,
            error: t('general_sessionTimeout_errorMessage'),
            isAuthError: true,
          });
        } else if (!isIgnoredError(error)) {
          setErrorConfig({ showError: true, error: error?.errorMessage });
        }

        return Promise.reject(getRejectReason({ response }));
        /* eslint-enable prefer-promise-reject-errors */
      },
      (errorResponse) => Promise.reject(errorResponse)
    );
  }, [t]);

  return (
    <SessionStateContext.Provider value={{ ...userInfo, menu, profileMenu, userName }}>
      <SessionDispatchContext.Provider
        value={{ setUserInfo, setMenu, setProfileMenu, setUserName }}
      >
        {errorConfig?.showError ? (
          <Modal
            description={errorConfig.error || ''}
            onCancel={errorConfig.isAuthError ? handleLogout : handleModalClose}
            submitText="OK"
            title="Error"
            type={ModalType.Error}
          />
        ) : null}
        {children}
      </SessionDispatchContext.Provider>
    </SessionStateContext.Provider>
  );
};

export const useUserInfoState = (): UserInfoState => {
  const context = useContext(SessionStateContext);
  if (context === undefined) {
    throw new Error('useUserInfoState must be used within a SessionProvider');
  }
  return {
    isLoggedIn: context.isLoggedIn,
    userCode: context.userCode,
    userProfileImageName: context.userProfileImageName,
    processDate: context.processDate,
    isAuthenticationRestricted: context.isAuthenticationRestricted,
  };
};

export const useUserInfoDispatch = (): UserInfoDispatch => {
  const context = useContext(SessionDispatchContext);
  if (context === undefined) {
    throw new Error('useUserInfoDispatch must be used within a SessionProvider');
  }
  return context.setUserInfo;
};

export const useMenuState = (): Menu[] => {
  const context = useContext(SessionStateContext);
  if (context === undefined) {
    throw new Error('useMenuState must be used within a SessionProvider');
  }
  return context.menu || [];
};

export const useProfileMenuState = (): ProfileMenuItem[] => {
  const context = useContext(SessionStateContext);
  if (context === undefined) {
    throw new Error('useProfileMenuState must be used within a SessionProvider');
  }
  return context?.profileMenu[0].subMenuList || [];
};

export const useMenuDispatch = (): MenuDispatch => {
  const context = useContext(SessionDispatchContext);
  if (context === undefined) {
    throw new Error('useMenuDispatch must be used within a SessionProvider');
  }
  return context.setMenu;
};

export const useProfileMenuDispatch = (): ProfileMenuDispatch => {
  const context = useContext(SessionDispatchContext);
  if (context === undefined) {
    throw new Error('useProfileMenuDispatch must be used within a SessionProvider');
  }
  return context.setProfileMenu;
};

export const useSubMenuState = (menuId: string): Menu[] => {
  const context = useContext(SessionStateContext);
  if (context === undefined) {
    throw new Error('useMenuState must be used within a SessionProvider');
  }
  return context?.menu?.find((menuItem) => menuItem.menuID === menuId)?.subMenuList || [];
};

export const useUserNameState = (): UserNameState => {
  const context = useContext(SessionStateContext);
  if (context === undefined) {
    throw new Error('useUserNameState must be used within a SessionProvider');
  }
  return context.userName || '';
};

export const useUserNameDispatch = (): UserNameDispatch => {
  const context = useContext(SessionDispatchContext);
  if (context === undefined) {
    throw new Error('useUserNameDispatch must be used within a SessionProvider');
  }
  return context.setUserName;
};

export default SessionProvider;
